import { useEffect, useState, useMemo, useRef } from "react";
import { Dialog, DialogContent } from "./ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import MapView from "./MapView";
import MediaPlayer from "./MediaPlayer";
import ElevationProfile from "./ElevationProfile";
import {
  Binoculars,
  Drop,
  FlagBanner,
  ForkKnife,
  MapPin,
  ShoppingCart,
  Warning,
  Wrench,
  X,
  Tent,
  Bed,
} from "@phosphor-icons/react";
import { api } from "../lib/api";

const fmt = (s) => {
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, "0")}`;
};

const fmtDistanceKm = (distanceKm) => {
  const value = Number(distanceKm);
  if (!Number.isFinite(value) || value <= 0) return "- km";
  const rounded = Math.round(value * 10) / 10;
  return `${rounded.toLocaleString()} km`;
};

const fmtElevationM = (elevationM) => {
  const value = Number(elevationM);
  if (!Number.isFinite(value) || value <= 0) return "- m";
  return `${Math.round(value).toLocaleString()} m`;
};

const POI_TYPE_META = {
  hazard: { label: "Hazard", Icon: Warning },
  water_source: { label: "Water Source", Icon: Drop },
  restaurant: { label: "Restaurant", Icon: ForkKnife },
  grocery_store: { label: "Grocery Store", Icon: ShoppingCart },
  bike_repair: { label: "Bike Repair", Icon: Wrench },
  transportation: { label: "Transportation", Icon: MapPin },
  scenic_viewpoint: { label: "Scenic Viewpoint", Icon: Binoculars },
  historical_marker: { label: "Historical Marker", Icon: FlagBanner },
  camping: {
    label: "Camping",
    iconName: "Tent",
    Icon: Tent,
  },
  lodging: {
    label: "Lodging",
    iconName: "Bed",
    Icon: Bed,
  },
  other: { label: "Other", Icon: MapPin },
};

const getPoiTypeMeta = (type) => POI_TYPE_META[type] || POI_TYPE_META.other;

const toRad = (deg) => (Number(deg) * Math.PI) / 180;

const haversineMeters = (a, b) => {
  if (!Array.isArray(a) || !Array.isArray(b)) return 0;
  const lat1 = Number(a[0]) || 0;
  const lng1 = Number(a[1]) || 0;
  const lat2 = Number(b[0]) || 0;
  const lng2 = Number(b[1]) || 0;
  const R = 6371000;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const x =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) *
      Math.cos(toRad(lat2)) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));
  return R * c;
};

const normalizeGpxPointsInput = (raw) => {
  const toPoints = (value) => {
    if (!Array.isArray(value)) return [];
    return value
      .filter((p) => Array.isArray(p) && p.length >= 2)
      .map((p) => p.map((v) => Number(v) || 0));
  };

  if (Array.isArray(raw)) return toPoints(raw);

  if (typeof raw === "string") {
    const trimmed = raw.trim();
    if (!trimmed) return [];
    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) return toPoints(parsed);
      if (parsed && typeof parsed === "object") {
        return toPoints(
          parsed.gpx_points || parsed.gpxPoints || parsed.points || [],
        );
      }
    } catch {
      return [];
    }
    return [];
  }

  if (raw && typeof raw === "object") {
    return toPoints(raw.gpx_points || raw.gpxPoints || raw.points || []);
  }

  return [];
};

export default function VideoPlayerModal({
  open,
  onOpenChange,
  video,
  route = null,
  playlists = [],
  videoGroups = [],
  onNextVideo = null,
}) {
  const playerRef = useRef(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(video?.duration || 0);
  const [fallbackRouteGPX, setFallbackRouteGPX] = useState([]);
  const [fallbackRoutePOIs, setFallbackRoutePOIs] = useState([]);
  const [focusedPoiId, setFocusedPoiId] = useState(null);
  const [poiFocusPosition, setPoiFocusPosition] = useState(null);
  const [hoveredRouteTime, setHoveredRouteTime] = useState(null);

  useEffect(() => {
    setCurrentTime(0);
    setDuration(video?.duration || 0);
    setFocusedPoiId(null);
    setPoiFocusPosition(null);
    setHoveredRouteTime(null);
  }, [video?.id, video?.duration]);

  const availablePlaylists = playlists.length > 0 ? playlists : videoGroups;
  const playlistID = video?.playlist_id || video?.group_id;
  // Determine fallback route GPX source: prefer playlist sequenced GPX files, then playlist GPX, then route GPX.
  const playlist =
    video && playlistID
      ? availablePlaylists.find((g) => g.id === playlistID)
      : null;
  const playlistVideos = useMemo(
    () => (Array.isArray(playlist?.videos) ? playlist.videos : []),
    [playlist],
  );
  const nextVideo = useMemo(() => {
    if (!video?.id || playlistVideos.length === 0) return null;
    const currentIndex = playlistVideos.findIndex((item) => item?.id === video.id);
    if (currentIndex < 0 || currentIndex >= playlistVideos.length - 1) return null;
    return playlistVideos[currentIndex + 1];
  }, [playlistVideos, video?.id]);
  const sequencedPlaylistPoints = (playlist?.gpx_files || [])
    .slice()
    .sort((a, b) => (a.sequence || 0) - (b.sequence || 0))
    .flatMap((f) => f.gpx_points || f.gpxPoints || []);
  const playlistPoints = useMemo(
    () => playlist?.gpx_points || playlist?.gpxPoints || [],
    [playlist],
  );
  const routePoints = useMemo(
    () => route?.gpx_points || route?.gpxPoints || [],
    [route],
  );
  const videoPoints = useMemo(() => {
    const gpxdataPoints = normalizeGpxPointsInput(video?.gpxdata);
    if (gpxdataPoints.length > 0) return gpxdataPoints;
    return normalizeGpxPointsInput(video?.gpx_points || video?.gpxPoints || []);
  }, [video]);

  useEffect(() => {
    let cancelled = false;

    const playlistHasGPX =
      sequencedPlaylistPoints.length > 0 || playlistPoints.length > 0;
    const routeHasGPX = routePoints.length > 0;

    if (playlistHasGPX || routeHasGPX || !video?.route_id) {
      setFallbackRouteGPX([]);
      setFallbackRoutePOIs([]);
      return () => {
        cancelled = true;
      };
    }

    api
      .get(`/routes/${video.route_id}`)
      .then(({ data }) => {
        if (cancelled) return;
        const points = data?.route?.gpx_points || data?.route?.gpxPoints || [];
        const routePois = data?.route?.pois || [];
        setFallbackRouteGPX(points);
        setFallbackRoutePOIs(routePois);
      })
      .catch(() => {
        if (!cancelled) {
          setFallbackRouteGPX([]);
          setFallbackRoutePOIs([]);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    playlistPoints.length,
    routePoints.length,
    sequencedPlaylistPoints.length,
    video?.route_id,
  ]);

  const inheritedRoutePoints = useMemo(
    () =>
      sequencedPlaylistPoints.length > 0
        ? sequencedPlaylistPoints
        : playlistPoints.length > 0
          ? playlistPoints
          : routePoints.length > 0
            ? routePoints
            : fallbackRouteGPX,
    [fallbackRouteGPX, playlistPoints, routePoints, sequencedPlaylistPoints],
  );

  const gpxPoints = useMemo(
    () => (videoPoints.length > 0 ? videoPoints : inheritedRoutePoints),
    [inheritedRoutePoints, videoPoints],
  );

  const secondaryGpxPoints = useMemo(
    () => (videoPoints.length > 0 ? inheritedRoutePoints : []),
    [inheritedRoutePoints, videoPoints],
  );

  const routePois = useMemo(
    () => route?.pois || fallbackRoutePOIs || [],
    [route?.pois, fallbackRoutePOIs],
  );

  const playlistPois = useMemo(() => playlist?.pois || [], [playlist?.pois]);
  const videoPois = useMemo(() => video?.pois || [], [video?.pois]);

  const pois = useMemo(() => {
    const withSource = (items, source) =>
      (items || []).map((p, idx) => ({
        ...p,
        id: `${source}_${p?.id || idx}`,
        __source: source,
        __sourceIndex: idx,
      }));

    const merged = [
      ...withSource(videoPois, "video"),
      ...withSource(playlistPois, "playlist"),
      ...withSource(routePois, "route"),
    ];

    if (!Array.isArray(gpxPoints) || gpxPoints.length === 0) {
      return merged;
    }

    const cumulativeMeters = [];
    let running = 0;
    for (let i = 0; i < gpxPoints.length; i++) {
      if (i > 0) {
        running += haversineMeters(gpxPoints[i - 1], gpxPoints[i]);
      }
      cumulativeMeters.push(running);
    }

    const nearestGpxIndex = (target) => {
      if (!target) return -1;
      let bestIdx = -1;
      let bestDist = Infinity;
      for (let i = 0; i < gpxPoints.length; i++) {
        const p = gpxPoints[i];
        const dLat = (Number(p?.[0]) || 0) - (Number(target[0]) || 0);
        const dLng = (Number(p?.[1]) || 0) - (Number(target[1]) || 0);
        const d = dLat * dLat + dLng * dLng;
        if (d < bestDist) {
          bestDist = d;
          bestIdx = i;
        }
      }
      return bestIdx;
    };

    return merged
      .map((p) => {
        const idx = nearestGpxIndex([p.lat, p.lng]);
        return {
          ...p,
          __routeIndex: idx,
          __routeMeters:
            idx >= 0 ? cumulativeMeters[idx] : Number.POSITIVE_INFINITY,
        };
      })
      .sort((a, b) => {
        if (a.__routeMeters !== b.__routeMeters) {
          return a.__routeMeters - b.__routeMeters;
        }
        return (a.__sourceIndex || 0) - (b.__sourceIndex || 0);
      });
  }, [videoPois, playlistPois, routePois, gpxPoints]);

  const markers = useMemo(() => video?.markers || [], [video?.markers]);
  const orderedMarkers = useMemo(
    () =>
      [...markers].sort(
        (a, b) => (Number(a?.from_sec) || 0) - (Number(b?.from_sec) || 0),
      ),
    [markers],
  );
  const timelineMarkers = useMemo(() => {
    if (!orderedMarkers || orderedMarkers.length === 0) return [];

    const totalDuration = Number(duration || video?.duration || 0);
    const routeStart =
      gpxPoints.length > 0 ? [gpxPoints[0][0], gpxPoints[0][1]] : null;
    const routeEnd =
      gpxPoints.length > 0
        ? [
            gpxPoints[gpxPoints.length - 1][0],
            gpxPoints[gpxPoints.length - 1][1],
          ]
        : null;

    const markerEndSec = (m) => {
      const from = Number(m?.from_sec) || 0;
      const to = Number(m?.to_sec);
      return Number.isFinite(to) ? Math.max(from, to) : from;
    };

    const markerStartPoint = (m) =>
      typeof m?.start_lat === "number" && typeof m?.start_lng === "number"
        ? [m.start_lat, m.start_lng]
        : null;

    const markerEndPoint = (m) =>
      typeof m?.end_lat === "number" && typeof m?.end_lng === "number"
        ? [m.end_lat, m.end_lng]
        : null;

    const out = [];

    const first = orderedMarkers[0];
    const firstFrom = Number(first?.from_sec) || 0;
    if (firstFrom > 0 && routeStart) {
      const firstStart = markerStartPoint(first) || routeEnd || routeStart;
      out.push({
        from_sec: 0,
        to_sec: firstFrom,
        start_lat: routeStart[0],
        start_lng: routeStart[1],
        end_lat: firstStart[0],
        end_lng: firstStart[1],
        radius_meters: null,
        __inferred: true,
      });
    }

    for (let i = 0; i < orderedMarkers.length; i++) {
      const current = orderedMarkers[i];
      out.push(current);

      const next = orderedMarkers[i + 1];
      if (!next) continue;

      const gapFrom = markerEndSec(current);
      const gapTo = Number(next?.from_sec) || 0;
      if (gapTo <= gapFrom) continue;

      const fromPoint =
        markerEndPoint(current) || routeStart || markerStartPoint(current);
      const toPoint =
        markerStartPoint(next) || routeEnd || markerEndPoint(next);
      if (!fromPoint || !toPoint) continue;

      out.push({
        from_sec: gapFrom,
        to_sec: gapTo,
        start_lat: fromPoint[0],
        start_lng: fromPoint[1],
        end_lat: toPoint[0],
        end_lng: toPoint[1],
        radius_meters: null,
        __inferred: true,
      });
    }

    const last = orderedMarkers[orderedMarkers.length - 1];
    const lastTo = markerEndSec(last);
    if (totalDuration > lastTo && routeEnd) {
      const lastEnd =
        markerEndPoint(last) || routeStart || markerStartPoint(last);
      if (lastEnd) {
        out.push({
          from_sec: lastTo,
          to_sec: totalDuration,
          start_lat: lastEnd[0],
          start_lng: lastEnd[1],
          end_lat: routeEnd[0],
          end_lng: routeEnd[1],
          radius_meters: null,
          __inferred: true,
        });
      }
    }

    return out.sort(
      (a, b) => (Number(a?.from_sec) || 0) - (Number(b?.from_sec) || 0),
    );
  }, [orderedMarkers, gpxPoints, duration, video?.duration]);
  const hasMarkers = markers.length > 0;

  const nearestGpxIndex = (target) => {
    if (!target || gpxPoints.length === 0) return -1;
    let bestIdx = -1;
    let bestDist = Infinity;
    for (let i = 0; i < gpxPoints.length; i++) {
      const p = gpxPoints[i];
      const dLat = p[0] - target[0];
      const dLng = p[1] - target[1];
      const d = dLat * dLat + dLng * dLng;
      if (d < bestDist) {
        bestDist = d;
        bestIdx = i;
      }
    }
    return bestIdx;
  };

  const poiSeekTime = (poi) => {
    if (!poi || gpxPoints.length === 0 || !(duration || video?.duration))
      return null;
    const idx = nearestGpxIndex([poi.lat, poi.lng]);
    if (idx < 0) return null;
    const dur = duration || video.duration || 0;
    if (dur <= 0) return null;
    return (idx / Math.max(1, gpxPoints.length - 1)) * dur;
  };

  const activePoi = useMemo(() => {
    if (
      !pois ||
      pois.length === 0 ||
      gpxPoints.length === 0 ||
      !(duration || video?.duration)
    ) {
      return null;
    }
    const dur = duration || video.duration || 0;
    if (dur <= 0) return null;

    const currentIdx = Math.round(
      (currentTime / dur) * Math.max(1, gpxPoints.length - 1),
    );
    const currentPoint =
      gpxPoints[Math.min(Math.max(0, currentIdx), gpxPoints.length - 1)];
    if (!currentPoint) return null;

    let bestPoi = null;
    let bestDist = Infinity;
    for (const p of pois) {
      const d = Math.hypot(
        (p.lat || 0) - currentPoint[0],
        (p.lng || 0) - currentPoint[1],
      );
      if (d < bestDist) {
        bestDist = d;
        bestPoi = p;
      }
    }
    return bestPoi;
  }, [pois, gpxPoints, currentTime, duration, video?.duration]);

  const highlightedPoiId = focusedPoiId || activePoi?.id || null;

  const clearPoiFocus = () => {
    setFocusedPoiId(null);
    setPoiFocusPosition(null);
  };

  const togglePoiFocus = (poi) => {
    if (!poi?.id) return;
    if (focusedPoiId === poi.id) {
      clearPoiFocus();
      return;
    }
    focusPoiInList(poi);
  };

  const focusPoiInList = (poi) => {
    if (!poi?.id) return;
    setFocusedPoiId(poi.id);
    if (poi.lat != null && poi.lng != null) {
      setPoiFocusPosition([poi.lat, poi.lng]);
    }
    requestAnimationFrame(() => {
      const el = document.getElementById(`poi-list-item-${poi.id}`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }
    });
  };

  // Find active marker for current time, if any
  const activeMarker = useMemo(() => {
    if (!timelineMarkers || timelineMarkers.length === 0) return null;

    const markerEndSec = (m) => {
      const from = Number(m?.from_sec) || 0;
      const to = Number(m?.to_sec);
      return Number.isFinite(to) ? Math.max(from, to) : from;
    };

    for (const m of timelineMarkers) {
      if (currentTime >= (m.from_sec || 0) && currentTime <= markerEndSec(m)) {
        return m;
      }
    }
    return null;
  }, [timelineMarkers, currentTime]);

  const activeMarkerSegment = useMemo(() => {
    if (!activeMarker) return null;
    return {
      start:
        activeMarker.start_lat != null
          ? [activeMarker.start_lat, activeMarker.start_lng]
          : null,
      end:
        activeMarker.end_lat != null
          ? [activeMarker.end_lat, activeMarker.end_lng]
          : null,
    };
  }, [activeMarker]);

  const activeMarkerFocus = useMemo(() => {
    if (!activeMarkerSegment) return null;
    const { start, end } = activeMarkerSegment;
    if (start && end) {
      return [(start[0] + end[0]) / 2, (start[1] + end[1]) / 2];
    }
    return start || end || null;
  }, [activeMarkerSegment]);

  const activeMarkerBounds = useMemo(() => {
    if (!activeMarkerSegment) return null;
    const { start, end } = activeMarkerSegment;
    if (!start || !end) return null;
    return [start, end];
  }, [activeMarkerSegment]);

  const activeMarkerPosition = useMemo(() => {
    if (!activeMarkerSegment) return null;
    return activeMarkerSegment.start || activeMarkerSegment.end || null;
  }, [activeMarkerSegment]);

  const activeMarkerRadius = useMemo(() => {
    const radius = Number(activeMarker?.radius_meters);
    if (!Number.isFinite(radius) || radius <= 0) return null;
    return radius;
  }, [activeMarker]);

  const hoveredRoutePosition = useMemo(() => {
    const hoverTime = Number(hoveredRouteTime);
    const totalDuration = duration || video?.duration || 0;
    if (
      !Number.isFinite(hoverTime) ||
      hoverTime < 0 ||
      gpxPoints.length === 0 ||
      totalDuration <= 0
    ) {
      return null;
    }

    const ratio = Math.min(1, Math.max(0, hoverTime / totalDuration));
    const idx = Math.min(
      gpxPoints.length - 1,
      Math.max(0, Math.round(ratio * Math.max(1, gpxPoints.length - 1))),
    );
    const point = gpxPoints[idx];
    return point ? [point[0], point[1]] : null;
  }, [duration, gpxPoints, hoveredRouteTime, video?.duration]);

  const handleHoverTime = (time) => {
    if (time == null || !Number.isFinite(Number(time))) {
      setHoveredRouteTime(null);
      return;
    }
    setHoveredRouteTime(Number(time));
  };

  const handleHoverIndex = (index) => {
    const totalDuration = duration || video?.duration || 0;
    if (
      index == null ||
      !Number.isFinite(Number(index)) ||
      totalDuration <= 0 ||
      gpxPoints.length === 0
    ) {
      setHoveredRouteTime(null);
      return;
    }
    const clamped = Math.min(gpxPoints.length - 1, Math.max(0, Number(index)));
    setHoveredRouteTime(
      (clamped / Math.max(1, gpxPoints.length - 1)) * totalDuration,
    );
  };

  const markerClickTargets = useMemo(() => {
    if (!timelineMarkers || timelineMarkers.length === 0) return [];

    const nearestGpxIndex = (target) => {
      if (!target || gpxPoints.length === 0) return -1;
      let bestIdx = -1;
      let bestDist = Infinity;
      for (let i = 0; i < gpxPoints.length; i++) {
        const p = gpxPoints[i];
        const dLat = p[0] - target[0];
        const dLng = p[1] - target[1];
        const d = dLat * dLat + dLng * dLng;
        if (d < bestDist) {
          bestDist = d;
          bestIdx = i;
        }
      }
      return bestIdx;
    };

    return timelineMarkers
      .map((m) => {
        const hasStart =
          typeof m.start_lat === "number" && typeof m.start_lng === "number";
        const hasEnd =
          typeof m.end_lat === "number" && typeof m.end_lng === "number";

        if (hasStart && hasEnd && gpxPoints.length > 1) {
          const startIdx = nearestGpxIndex([m.start_lat, m.start_lng]);
          const endIdx = nearestGpxIndex([m.end_lat, m.end_lng]);
          if (startIdx >= 0 && endIdx >= 0) {
            const from = Math.min(startIdx, endIdx);
            const to = Math.max(startIdx, endIdx);
            if (to > from) {
              return {
                kind: "section",
                from,
                to,
                fromSec: Number(m.from_sec) || 0,
                toSec: Number(m.to_sec) || Number(m.from_sec) || 0,
              };
            }
          }
        }

        if (hasStart || hasEnd) {
          const point = hasStart
            ? [m.start_lat, m.start_lng]
            : [m.end_lat, m.end_lng];
          return {
            kind: "location",
            point,
            fromSec: Number(m.from_sec) || 0,
            toSec: Number(m.to_sec) || Number(m.from_sec) || 0,
          };
        }

        return null;
      })
      .filter(Boolean);
  }, [gpxPoints, timelineMarkers]);

  const normalizeSeekTime = (seconds) => {
    if (!markerClickTargets || markerClickTargets.length === 0) {
      return seconds;
    }
    const t = Number(seconds);
    if (!Number.isFinite(t)) return seconds;

    for (const target of markerClickTargets) {
      if (target.kind !== "section") continue;
      const from = Number(target.fromSec) || 0;
      const rawTo = Number(target.toSec);
      const to = Number.isFinite(rawTo) ? Math.max(from, rawTo) : from;
      if (t >= from && t < to) {
        return from;
      }
    }

    return seconds;
  };

  const elevationSectionLines = useMemo(() => {
    if (!markerClickTargets || markerClickTargets.length === 0) return [];
    const values = new Set();
    for (const target of markerClickTargets) {
      if (target.kind !== "section") continue;
      const from = Number(target.fromSec);
      const to = Number(target.toSec);
      if (Number.isFinite(from)) values.add(Math.round(from));
      if (Number.isFinite(to)) values.add(Math.round(to));
    }
    return Array.from(values).sort((a, b) => a - b);
  }, [markerClickTargets]);

  const seekTo = (seconds) => {
    const normalized = normalizeSeekTime(seconds);
    setCurrentTime(normalized);
    playerRef.current?.seekTo(normalized);
  };

  const handleMapClick = ({ lat, lng }) => {
    let bestTime = null;
    let bestDist = Infinity;
    //console.log("map click at "+lat+","+lng +" with "+markerClickTargets.length+" markers and "+pois.length+" pois")
    for (const target of markerClickTargets) {
      if (target.kind === "location") {
        //console.log("checking location")
        const d = Math.hypot(target.point[0] - lat, target.point[1] - lng);
        if (d < bestDist) {
          bestDist = d;
          bestTime = target.fromSec;
          //  console.log("found better location: "+ target.fromSec+ " at dist "+d)
        }
        continue;
      }

      if (target.kind === "section") {
        // console.log("checking section")
        let bestSectionDist = Infinity;
        // console.log("checking section from "+target.from+" to "+target.to+ " at fromSec "+target.fromSec+" toSec "+target.toSec)
        for (let i = target.from; i <= target.to; i++) {
          const [plat, plng] = gpxPoints[i];
          const d = Math.hypot(plat - lat, plng - lng);
          if (d < bestSectionDist) {
            bestSectionDist = d;
          }
        }

        if (bestSectionDist < bestDist) {
          //console.log("found better section: "+ target.fromSec + " at dist "+bestSectionDist)
          bestDist = bestSectionDist;
          bestTime = target.fromSec;
        }
      }
    }

    for (const p of pois) {
      //  console.log("checking poi "+p.id)
      const d = Math.hypot(p.lat - lat, p.lng - lng);
      if (d < bestDist) {
        const t = poiSeekTime(p);
        if (t != null) {
          // console.log("found better poi: "+ p.id+ "at " + t + " at dist "+d)
          bestDist = d;
          bestTime = t;
        }
      }
    }
    if (gpxPoints.length > 0 && (duration || video.duration)) {
      const dur = duration || video.duration;
      for (let i = 0; i < gpxPoints.length; i++) {
        const [plat, plng] = gpxPoints[i];
        const d = Math.hypot(plat - lat, plng - lng);
        if (d < bestDist) {
          bestDist = d;
          bestTime = (i / (gpxPoints.length - 1)) * dur;
          //   console.log("found better gpx: "+ bestTime+ "at dist "+d)
        }
      }
    }
    //console.log("seeking to "+bestTime)
    if (bestTime != null) seekTo(bestTime);
  };

  const elevationSeries = useMemo(() => {
    const totalDuration = duration || video?.duration || 0;
    if (!gpxPoints || gpxPoints.length === 0) return [];

    const baseSeries = gpxPoints
      .map((p, i) => {
        if (!Array.isArray(p) || p.length < 3) return null;
        const t =
          totalDuration > 0
            ? (i / Math.max(1, gpxPoints.length - 1)) * totalDuration
            : i;
        return { t, ele: p[2] };
      })
      .filter(Boolean);

    if (!timelineMarkers || timelineMarkers.length === 0) {
      return baseSeries;
    }

    const nearestGpxIndex = (target) => {
      if (!target || gpxPoints.length === 0) return -1;
      let bestIdx = -1;
      let bestDist = Infinity;
      for (let i = 0; i < gpxPoints.length; i++) {
        const p = gpxPoints[i];
        const dLat = p[0] - target[0];
        const dLng = p[1] - target[1];
        const d = dLat * dLat + dLng * dLng;
        if (d < bestDist) {
          bestDist = d;
          bestIdx = i;
        }
      }
      return bestIdx;
    };

    const nearestElevation = (index) => {
      if (index < 0 || index >= gpxPoints.length) return null;
      if (gpxPoints[index] && gpxPoints[index].length >= 3) {
        return gpxPoints[index][2];
      }
      for (let offset = 1; offset < gpxPoints.length; offset++) {
        const left = index - offset;
        const right = index + offset;
        if (left >= 0 && gpxPoints[left] && gpxPoints[left].length >= 3) {
          return gpxPoints[left][2];
        }
        if (
          right < gpxPoints.length &&
          gpxPoints[right] &&
          gpxPoints[right].length >= 3
        ) {
          return gpxPoints[right][2];
        }
      }
      return null;
    };

    const points = [];
    for (const m of timelineMarkers) {
      const fromSec = Number(m?.from_sec) || 0;
      const rawToSec = Number(m?.to_sec);
      const toSec = Number.isFinite(rawToSec)
        ? Math.max(fromSec, rawToSec)
        : fromSec;

      const hasStart =
        typeof m?.start_lat === "number" && typeof m?.start_lng === "number";
      const hasEnd =
        typeof m?.end_lat === "number" && typeof m?.end_lng === "number";

      if (hasStart && hasEnd) {
        const startIdx = nearestGpxIndex([m.start_lat, m.start_lng]);
        const endIdx = nearestGpxIndex([m.end_lat, m.end_lng]);
        if (startIdx >= 0 && endIdx >= 0 && startIdx !== endIdx) {
          const from = Math.min(startIdx, endIdx);
          const to = Math.max(startIdx, endIdx);
          const segmentElevations = gpxPoints
            .slice(from, to + 1)
            .map((p) => (Array.isArray(p) && p.length >= 3 ? p[2] : null))
            .filter((v) => v != null);
          if (segmentElevations.length > 0) {
            const segmentSpan = Math.max(1, segmentElevations.length - 1);
            segmentElevations.forEach((ele, idx) => {
              const progress = idx / segmentSpan;
              points.push({
                t: fromSec + (toSec - fromSec) * progress,
                ele,
              });
            });
            continue;
          }
        }
      }

      // Stationary markers (or incomplete lat/lon) should appear flat.
      const location = hasStart
        ? [m.start_lat, m.start_lng]
        : hasEnd
          ? [m.end_lat, m.end_lng]
          : null;
      if (!location) continue;

      const idx = nearestGpxIndex(location);
      const ele = nearestElevation(idx);
      if (ele == null) continue;
      points.push({ t: fromSec, ele });
      if (toSec > fromSec) {
        points.push({ t: toSec, ele });
      }
    }

    if (points.length === 0) {
      if (baseSeries.length > 0) {
        return baseSeries;
      }

      // Fallback for marker-only videos with no GPX elevation values.
      // Keep a flat, time-aligned profile so users can still scrub markers.
      const fallbackTimes = timelineMarkers
        .flatMap((m) => {
          const from = Number(m?.from_sec) || 0;
          const rawTo = Number(m?.to_sec);
          const to = Number.isFinite(rawTo) ? Math.max(from, rawTo) : from;
          return [from, to];
        })
        .filter((t) => Number.isFinite(t));

      if (Number.isFinite(totalDuration) && totalDuration > 0) {
        fallbackTimes.push(0, totalDuration);
      }

      const uniqueSortedTimes = Array.from(new Set(fallbackTimes)).sort(
        (a, b) => a - b,
      );
      return uniqueSortedTimes.map((t, idx) => ({
        t,
        ele: 0,
        idx,
      }));
    }

    points.sort((a, b) => a.t - b.t);
    return points;
  }, [duration, gpxPoints, timelineMarkers, video?.duration]);

  const hasElevation = elevationSeries.length > 0;

  if (!video) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-[1400px] w-[95vw] h-[92vh] max-h-[92vh] p-0 bg-[#1A1D1A] text-white border-0 overflow-hidden rounded-2xl"
        data-testid="video-player-modal"
      >
        <button
          type="button"
          onClick={() => onOpenChange(false)}
          className="absolute top-4 right-4 z-50 p-2 bg-black/60 hover:bg-black/80 rounded-full transition-colors"
          data-testid="video-close-button"
          aria-label="Close"
        >
          <X size={20} />
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-5 h-full min-h-0 overflow-hidden">
          {/* Video left */}
          <div className="lg:col-span-3 bg-black flex flex-col min-h-0 h-full overflow-hidden">
            <div className="flex-1 flex items-center justify-center bg-black min-h-0 overflow-hidden">
              <MediaPlayer
                ref={playerRef}
                src={video.video_url}
                poster={video.thumbnail}
                onTimeUpdate={setCurrentTime}
                onDuration={setDuration}
              />
            </div>

            {hasElevation && (
              <ElevationProfile
                gpxPoints={gpxPoints}
                series={elevationSeries}
                sectionLines={elevationSectionLines}
                duration={duration || video.duration || 0}
                currentTime={currentTime}
                highlightTime={hoveredRouteTime}
                onSeek={seekTo}
                onHoverTime={handleHoverTime}
              />
            )}

            <div className="p-4 border-t border-white/10">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#C86B4E]">
                    {video.creator}
                  </div>
                  <h3 className="font-display font-black text-lg mt-1">
                    {video.title}
                  </h3>
                  <div className="text-xs text-white/60 mt-1">
                    {fmt(currentTime)} / {fmt(duration || video.duration || 0)}
                  </div>
                  <div className="text-xs text-white/70 mt-1">
                    {fmtDistanceKm(video.distance_km)} · {fmtElevationM(video.elevation_m)}
                  </div>
                </div>

                {nextVideo && (
                  <button
                    type="button"
                    onClick={() => onNextVideo?.(nextVideo)}
                    aria-label="Next video"
                    className="inline-flex items-center justify-center rounded-full bg-[#C86B4E] px-3 py-2 text-sm font-bold text-white hover:bg-[#b55f42] transition-colors"
                  >
                    Next video
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Map + POI list right */}
          <div className="lg:col-span-2 flex flex-col bg-[#F9F6F0] text-[#1A1D1A] min-h-0">
            <div className="h-1/2 border-b border-[#E5E0D8]">
              <MapView
                gpxPoints={gpxPoints}
                secondaryGpxPoints={secondaryGpxPoints}
                pois={pois}
                activePoiId={highlightedPoiId}
                markerSegment={activeMarkerSegment}
                markerPosition={activeMarkerPosition}
                markerRadius={activeMarkerRadius}
                focusPosition={poiFocusPosition || activeMarkerFocus}
                focusBounds={poiFocusPosition ? null : activeMarkerBounds}
                highlightFullRoute={!hasMarkers && gpxPoints.length > 1}
                highlightPosition={hoveredRoutePosition}
                onHoverIndex={handleHoverIndex}
                onMapClick={handleMapClick}
                onPoiClick={(p) => {
                  togglePoiFocus(p);
                }}
                height="100%"
                fit
              />
            </div>

            <div className="flex-1 min-h-0 p-5 overflow-hidden">
              <Tabs
                defaultValue="markers"
                className="h-full min-h-0 flex flex-col"
              >
                <TabsList className="grid w-full grid-cols-2 bg-[#ECE6DC] shrink-0">
                  <TabsTrigger value="markers" data-testid="marker-tab-trigger">
                    Markers
                  </TabsTrigger>
                  <TabsTrigger value="pois" data-testid="poi-tab-trigger">
                    POIs
                  </TabsTrigger>
                </TabsList>

                <TabsContent
                  value="pois"
                  className="mt-4 flex-1 min-h-0 overflow-hidden data-[state=inactive]:hidden"
                >
                  <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#C86B4E] mb-3">
                    Points of Interest
                  </div>
                  <div className="h-full min-h-0 overflow-y-auto no-scrollbar pr-1">
                    <ul className="space-y-2" data-testid="poi-list">
                      {pois.map((p) => {
                        const isActive = highlightedPoiId === p.id;
                        const typeMeta = getPoiTypeMeta(p?.type);
                        const TypeIcon = typeMeta.Icon;
                        const sourceLabel =
                          p.__source === "video"
                            ? "Video"
                            : p.__source === "playlist"
                              ? "Playlist"
                              : "Route";
                        return (
                          <li key={p.id} id={`poi-list-item-${p.id}`}>
                            <button
                              type="button"
                              onClick={() => {
                                togglePoiFocus(p);
                              }}
                              data-testid={`poi-item-${p.id}`}
                              className={`w-full text-left flex items-start gap-3 p-3 rounded-xl border transition-all ${
                                isActive
                                  ? "bg-[#C86B4E]/10 border-[#C86B4E]"
                                  : "bg-white border-[#E5E0D8] hover:border-[#2C5545]"
                              }`}
                            >
                              <div
                                className={`mt-0.5 w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                                  isActive
                                    ? "bg-[#C86B4E] text-white"
                                    : "bg-[#F9F6F0] text-[#2C5545]"
                                }`}
                              >
                                <TypeIcon size={16} weight="fill" />
                              </div>
                              <div className="flex-1 min-w-0">
                                {!isActive ? (
                                  <div className="text-sm text-[#1A1D1A] truncate">
                                    <span className="font-bold">{p.title}</span>
                                    <span className="text-[#5C665F]">
                                      {" "}
                                      · {typeMeta.label} · {sourceLabel}
                                    </span>
                                  </div>
                                ) : (
                                  <>
                                    <div className="font-bold text-sm truncate">
                                      {p.title}
                                    </div>
                                    <div className="text-[11px] text-[#5C665F] mt-0.5 inline-flex items-center gap-1">
                                      <TypeIcon size={11} weight="fill" />
                                      {typeMeta.label}
                                    </div>
                                    <div className="text-[11px] text-[#5C665F] mt-0.5">
                                      {sourceLabel}
                                    </div>
                                    {p.description && (
                                      <div className="text-xs text-[#5C665F] mt-1 line-clamp-2">
                                        {p.description}
                                      </div>
                                    )}
                                    {p.url && (
                                      <a
                                        href={p.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-xs text-[#2C5545] underline mt-1 block truncate"
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        {p.url}
                                      </a>
                                    )}
                                    {p.images && p.images.length > 0 && (
                                      <div className="flex gap-1 mt-1 flex-wrap">
                                        {p.images.map((src, i) => (
                                          <img
                                            key={i}
                                            src={src}
                                            alt=""
                                            className="w-12 h-12 object-cover rounded"
                                          />
                                        ))}
                                      </div>
                                    )}
                                  </>
                                )}
                              </div>
                            </button>
                          </li>
                        );
                      })}
                      {pois.length === 0 && (
                        <li className="text-sm text-[#5C665F]">
                          No points of interest yet.
                        </li>
                      )}
                    </ul>
                  </div>
                </TabsContent>

                <TabsContent
                  value="markers"
                  className="mt-4 flex-1 min-h-0 overflow-hidden data-[state=inactive]:hidden"
                >
                  <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#C86B4E] mb-3">
                    Timestamped Markers
                  </div>
                  <div className="h-full min-h-0 overflow-y-auto no-scrollbar pr-1">
                    <ul className="space-y-2" data-testid="marker-list">
                      {orderedMarkers.map((m, idx) => {
                        const from = Number(m?.from_sec) || 0;
                        const rawTo = Number(m?.to_sec);
                        const to = Number.isFinite(rawTo)
                          ? Math.max(from, rawTo)
                          : from;
                        const isActive =
                          currentTime >= from && currentTime <= to;
                        const hasSpan = to > from;
                        const hasStart =
                          typeof m?.start_lat === "number" &&
                          typeof m?.start_lng === "number";
                        const hasEnd =
                          typeof m?.end_lat === "number" &&
                          typeof m?.end_lng === "number";
                        return (
                          <li key={`marker-${idx}-${from}-${to}`}>
                            <button
                              type="button"
                              className={`w-full text-left p-3 rounded-xl border transition-all ${
                                isActive
                                  ? "bg-[#2C5545]/10 border-[#2C5545]"
                                  : "bg-white border-[#E5E0D8] hover:border-[#2C5545]"
                              }`}
                              data-testid={`marker-item-${idx}`}
                              onClick={() => {
                                setPoiFocusPosition(null);
                                seekTo(from);
                              }}
                            >
                              <div className="flex items-center justify-between gap-2">
                                <div className="font-bold text-sm text-[#1A1D1A]">
                                  {hasSpan
                                    ? `${fmt(from)} - ${fmt(to)}`
                                    : fmt(from)}
                                </div>
                                <div className="text-xs text-[#5C665F]">
                                  {hasStart && hasEnd ? "Section" : "Location"}
                                </div>
                              </div>
                              <div className="text-[11px] text-[#5C665F] mt-1">
                                {hasStart && hasEnd
                                  ? `${m.start_lat.toFixed(5)}, ${m.start_lng.toFixed(5)} -> ${m.end_lat.toFixed(5)}, ${m.end_lng.toFixed(5)}`
                                  : hasStart
                                    ? `${m.start_lat.toFixed(5)}, ${m.start_lng.toFixed(5)}`
                                    : hasEnd
                                      ? `${m.end_lat.toFixed(5)}, ${m.end_lng.toFixed(5)}`
                                      : "No coordinates"}
                              </div>
                            </button>
                          </li>
                        );
                      })}
                      {orderedMarkers.length === 0 && (
                        <li className="text-sm text-[#5C665F]">
                          No markers available.
                        </li>
                      )}
                    </ul>
                  </div>
                </TabsContent>
              </Tabs>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
